import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { alpacaService, Account, Order } from '../../services/AlpacaService';
import { isOptionSymbol } from '../../utils/symbolUtils';
import { TradeStats, computeRoundTripStats } from '../../utils/tradeUtils';
import { useAccountMode } from '../../context/AccountModeContext';
import styles from './Reports.module.css';

type DatePreset = 'today' | '7d' | '30d' | '90d' | 'all' | 'custom';

function getDatePresetRange(preset: DatePreset): { after?: string; until?: string } {
  if (preset === 'all') return {};
  const now = new Date();
  const until = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  let start: Date;
  switch (preset) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '7d':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    default:
      return {};
  }
  return {
    after: start.toISOString(),
    until: until.toISOString(),
  };
}

const Reports: React.FC = () => {
  const { mode } = useAccountMode();
  const [account, setAccount] = useState<Account | null>(null);
  const [stats, setStats] = useState<TradeStats | null>(null);
  const [totalOrders, setTotalOrders] = useState(0);
  const [filledCount, setFilledCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Determine date range
        let after: string | undefined;
        let until: string | undefined;

        if (datePreset === 'custom') {
          if (startDate) after = new Date(startDate).toISOString();
          if (endDate) {
            const end = new Date(endDate);
            end.setDate(end.getDate() + 1);
            until = end.toISOString();
          }
        } else {
          const range = getDatePresetRange(datePreset);
          after = range.after;
          until = range.until;
        }

        const [acct, ordersData] = await Promise.all([
          alpacaService.getAccount(mode),
          alpacaService.getOrders('all', '500', after, until, mode),
        ]);

        // Filter out options contracts — they have their own page
        const stockOrders = ordersData.orders.filter((o: Order) => !isOptionSymbol(o.symbol));
        const filled = stockOrders.filter((o: Order) => o.status === 'filled');

        setAccount(acct);
        setTotalOrders(stockOrders.length);
        setFilledCount(filled.length);
        setStats(computeRoundTripStats(stockOrders));
        setError('');
      } catch (err: any) {
        setError(err.message || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [datePreset, startDate, endDate, mode]);

  const fmt = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const pnlClass = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return num > 0 ? styles.positive : num < 0 ? styles.negative : '';
  };

  if (loading) {
    return (
      <section className={styles.page}>
        <p className={styles.loading}>Generating report...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.page}>
        <p className={styles.error}>{error}</p>
        <Link to="/home" className={styles.back}>
          &larr; Back to Home
        </Link>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Reports</h1>
      <p className={styles.subtitle}>Stock trades only &bull; Options tracked separately</p>

      {/* Date Presets */}
      <div className={styles.dateRow}>
        <div className={styles.datePresets}>
          {(
            [
              ['today', 'Today'],
              ['7d', '7 Days'],
              ['30d', '30 Days'],
              ['90d', '90 Days'],
              ['all', 'All Time'],
              ['custom', 'Custom'],
            ] as [DatePreset, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              className={`${styles.presetBtn} ${datePreset === key ? styles.activePreset : ''}`}
              onClick={() => {
                setDatePreset(key);
                if (key !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {datePreset === 'custom' && (
          <div className={styles.dateFilter}>
            <span className={styles.dateLabel}>From</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={styles.dateInput}
              title="Report start date"
            />
            <span className={styles.dateLabel}>To</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={styles.dateInput}
              title="Report end date"
            />
            {(startDate || endDate) && (
              <button
                className={styles.clearBtn}
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
              >
                &times;
              </button>
            )}
          </div>
        )}
      </div>

      {account && (
        <div className={styles.accountBar}>
          <span>
            Equity: <strong>${fmt(account.equity)}</strong>
          </span>
          <span>
            Status: <strong>{account.status}</strong>
          </span>
          <span>
            Day Trades: <strong>{account.daytrade_count}</strong>
          </span>
          <span>
            Orders: <strong>{totalOrders}</strong> ({filledCount} filled)
          </span>
        </div>
      )}

      {stats && (
        <>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Round Trips</span>
              <span className={styles.statValue}>{stats.totalTrades}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Win Rate</span>
              <span
                className={`${styles.statValue} ${parseFloat(stats.winRate) >= 50 ? styles.positive : styles.negative}`}
              >
                {stats.winRate}%
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total P&amp;L</span>
              <span className={`${styles.statValue} ${pnlClass(stats.totalPnl)}`}>${fmt(stats.totalPnl)}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Profit Factor</span>
              <span className={styles.statValue}>{stats.profitFactor}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Wins / Losses</span>
              <span className={styles.statValue}>
                <span className={styles.positive}>{stats.wins}</span>
                {' / '}
                <span className={styles.negative}>{stats.losses}</span>
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Avg Win / Avg Loss</span>
              <span className={styles.statValue}>
                <span className={styles.positive}>${fmt(stats.avgWin)}</span>
                {' / '}
                <span className={styles.negative}>${fmt(stats.avgLoss)}</span>
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Best Trade</span>
              <span className={`${styles.statValue} ${pnlClass(stats.bestTrade)}`}>${fmt(stats.bestTrade)}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Worst Trade</span>
              <span className={`${styles.statValue} ${pnlClass(stats.worstTrade)}`}>${fmt(stats.worstTrade)}</span>
            </div>
          </div>

          <h2 className={styles.sectionTitle}>Performance by Symbol</h2>
          {stats.symbolBreakdown.length === 0 ? (
            <p className={styles.empty}>No completed round-trips yet</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Round Trips</th>
                    <th>Wins</th>
                    <th>Losses</th>
                    <th>Win Rate</th>
                    <th>P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.symbolBreakdown.map((s) => {
                    const wr = s.trades > 0 ? ((s.wins / s.trades) * 100).toFixed(1) : '0';
                    return (
                      <tr key={s.symbol}>
                        <td className={styles.symbol}>{s.symbol}</td>
                        <td>{s.trades}</td>
                        <td className={styles.positive}>{s.wins}</td>
                        <td className={styles.negative}>{s.losses}</td>
                        <td className={parseFloat(wr) >= 50 ? styles.positive : styles.negative}>{wr}%</td>
                        <td className={pnlClass(s.pnl)}>${fmt(s.pnl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className={styles.totalRow}>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td>
                      <strong>{stats.totalTrades}</strong>
                    </td>
                    <td className={styles.positive}>
                      <strong>{stats.wins}</strong>
                    </td>
                    <td className={styles.negative}>
                      <strong>{stats.losses}</strong>
                    </td>
                    <td className={parseFloat(stats.winRate) >= 50 ? styles.positive : styles.negative}>
                      <strong>{stats.winRate}%</strong>
                    </td>
                    <td className={pnlClass(stats.totalPnl)}>
                      <strong>${fmt(stats.totalPnl)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      <Link to="/home" className={styles.back}>
        &larr; Back to Home
      </Link>
    </section>
  );
};

export default Reports;
